import { Component, OnDestroy, OnInit } from '@angular/core';
import { MetricsDataService } from './service/metrics-data.service';
import { MetricsToolbarFilters } from './model/metrics-toolbar.model';
import { QCWeekData } from './model/user-qc-time.model';
import { Subject, takeUntil } from 'rxjs';
import { MetricsViewModeEnum } from './components/table-toolbar/enum/metrics-toolbar.enum';

@Component({
    selector: 'cv-metrics-dashboard',
    templateUrl: 'metrics-dashboard.component.html',
    styleUrls: ['metrics-dashboard.component.scss'],
})
export class MetricsDashboardComponent implements OnInit, OnDestroy {
    constructor(private metricsDataService: MetricsDataService) {}
    weekData: QCWeekData[] = [];
    private atComponentDestroy = new Subject();
    public selectedView: MetricsViewModeEnum = MetricsViewModeEnum.GRID;
    ngOnInit() {
        // Subscribe to per-week metrics data
        this.metricsDataService.qcMetricsPerWeek$
            .pipe(takeUntil(this.atComponentDestroy))
            .subscribe((data) => {
                if (data) {
                    this.weekData = data;
                }
            });
    }

    ngOnDestroy(): void {
        this.atComponentDestroy.next({});
        this.atComponentDestroy.unsubscribe();
    }
    
    /**
     * Handle changes from the filters toolbar.
     *
     * @param filters The set of metrics toolbar filters that were just applied.
     * @returns {void}
     */
    onFiltersChange(filters: MetricsToolbarFilters) {
        this.metricsDataService.fetchQCItems(filters);
    }

    /**
     * Handle switching between the “Graphs” and “Table” views.
     *
     * @param viewMode The newly selected view mode (graphs or table).
     * @returns {void}
     */
    onViewMetricsViewChange(viewMode: MetricsViewModeEnum): void {
        this.selectedView = viewMode;
    }

    get weekLabels() {
        return this.weekData.map((d) =>
            new Date(d.weekStart).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
        );
    }

    get totalReviewedChart() {
        return {
            type: 'bar',
            data: {
                labels: this.weekLabels,
                datasets: [
                    { label: 'Total Reviewed', data: this.weekData.map((d) => d.totalReviewed) },
                ],
            },
        };
    }

    get rejectedChart() {
        return {
            type: 'bar',
            data: {
                labels: this.weekLabels,
                datasets: [
                    {
                        label: 'Rejected by QC Reviewer',
                        data: this.weekData.map((d) => d.totalRejectedByQCReviewer),
                    },
                    {
                        label: 'Rejected by Client',
                        data: this.weekData.map((d) => d.totalRejectedByClient),
                    },
                ],
            },
            options: {
                plugins: { legend: { position: 'top' } },
                scales: { x: { stacked: true }, y: { stacked: true } },
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
            },
        };
    }

    get avgTimesChart() {
        return {
            type: 'line',
            data: {
                labels: this.weekLabels,
                datasets: [
                    {
                        label: 'Avg Waiting',
                        data: this.weekData.map((d) => d.avgMinutesWaitingForReview),
                        tension: 0.3,
                    },
                    {
                        label: 'Avg In Review',
                        data: this.weekData.map((d) => d.avgMinutesInReview),
                        tension: 0.3,
                    },
                    {
                        label: 'Avg Active Work',
                        data: this.weekData.map((d) => d.avgMinutesActiveWorkInReview),
                        tension: 0.3,
                    },
                ],
            },
        };
    }

    get revisionRateChart() {
        return {
            type: 'line',
            data: {
                labels: this.weekLabels,
                datasets: [
                    {
                        label: 'Revision Rate (%)',
                        data: this.weekData.map((d) => d.revisionRate),
                        tension: 0.3,
                    },
                    {
                        label: 'Client Revision Rate (%)',
                        data: this.weekData.map((d) => d.clientRevisionRate),
                        tension: 0.3,
                    },
                ],
            },
        };
    }
}
